<template>
  <div class="form-container request-content-form">
    <FormKit type="form" @submit="handleFormSubmit" :actions="false" v-slot="{ state }">
      <div class="form-body" data-testid="request-content-form-body">
        <p class="form-description">
          {{ formDescription }}
        </p>

        <!-- Content Type Selector -->
        <ContentTypeSelector v-model="formData.contentType" :disabled="edit" data-testid="content-type-selector" />

        <!-- Title Field with Inline Edit -->
        <div class="form-group">
          <label class="form-label">Title</label>

          <!-- Display Mode -->
          <div v-if="!inlineTitleEdit" class="inline-display-mode" @click="startEditingTitle">
            <p class="display-text">{{ formData.name }}</p>
            <Icon name="general.edited" size="sm" class="edit-icon" />
          </div>

          <!-- Edit Mode -->
          <div v-else>
            <FormKit
              type="text"
              name="name"
              placeholder='E.g. "Testimonial Request" or "Button Troubleshooting"'
              v-model="formData.name"
              :validation="[['required'], ['matches', /^(?!\s+$).*$/], ['length', 0, 100]]"
              validation-visibility="live"
              :validation-messages="{
                required: 'Title is required',
                matches: 'Title cannot contain only whitespace',
                length: 'Title cannot exceed 100 characters',
              }"
              data-testid="name-input"
              outer-class="!mb-0"
            />
          </div>
        </div>

        <!-- Message Field with Inline Edit -->
        <div class="form-group">
          <label class="form-label">Message <span class="optional-text">(Optional)</span></label>

          <!-- Display Mode -->
          <div v-if="!inlineMessageEdit" class="inline-display-mode" @click="startEditingMessage">
            <em v-if="!formData.message || formData.message.length === 0" class="display-text-empty">Add a message</em>
            <p v-else class="display-text">{{ formData.message }}</p>
            <Icon name="general.edited" size="sm" class="edit-icon" />
          </div>

          <!-- Edit Mode -->
          <div v-else>
            <p class="help-text">This message will be shown to users when they visit your request link.</p>
            <FormKit
              type="textarea"
              name="message"
              :placeholder="messagePlaceholder"
              v-model="formData.message"
              rows="4"
              validation="length:0,500"
              validation-visibility="live"
              :validation-messages="{
                length: 'Message cannot exceed 500 characters',
              }"
              outer-class="!mb-0"
            />
          </div>
        </div>

        <!-- Custom ID Field -->
        <div class="form-group">
          <label class="form-label">ID <span class="optional-text">(Optional)</span></label>
          <FormKit
            type="text"
            name="customId"
            placeholder="E.g. ticket number"
            v-model="formData.customId"
            validation="length:0,50|matches:/^[a-zA-Z0-9-_]*$/"
            validation-visibility="live"
            :validation-messages="{
              length: 'ID cannot exceed 50 characters',
              matches: 'Only letters, numbers, hyphens, and underscores allowed',
            }"
          />
        </div>

        <CustomFieldsBuilder v-model="formData.customFields" />

        <!-- Collect Logs Toggle (only shown for video content type) -->
        <div v-if="logCollectionEnabled && formData.contentType === 'video'" class="form-group collect-logs-group">
          <FormKit type="toggle" name="collectLogs" label="Ask to collect logs" v-model="formData.collectLogs" outer-class="!mb-0" />

          <div v-if="formData.collectLogs" class="collect-logs-info">
            <div v-if="!requestContentLogEnabled" class="warning-message">
              <Icon name="general.warning" size="sm" class="warning-icon" />
              <span
                >You need to add a log collection URL to use this feature.
                <a href="#" class="warning-link" @click.prevent="gotoRequestContentSettings">Configure settings</a></span
              >
            </div>
            <div v-else-if="hasMultipleDomains" class="w-full">
              <FormKit
                type="menuselect"
                name="logCollectionDomain"
                label="Recording URL"
                v-model="selectedLogCollectionDomain"
                :options="logCollectionDomains"
                data-testid="log-collection-domain-select"
                :teleport-menu="true"
                max-width="400px"
                :truncate-text="false"
                outer-class="!w-full"
              />
            </div>
            <div v-else class="log-collection-domain">
              <span>Logs will be collected from:</span>
              <span class="domain-text">
                <strong>{{ selectedLogCollectionDomain || requestContentLogDomain }}</strong>
              </span>
            </div>
          </div>
        </div>

        <!-- Expiration and Collection Row -->
        <div class="form-row-grid">
          <div class="form-field-wrapper">
            <FormKit
              type="menuselect"
              name="expiresOption"
              label="Expires"
              v-model="formData.expiresOption"
              :options="expirationOptions"
              outer-class="inline-field"
              :teleport-menu="true"
              data-testid="expires-select"
            />
          </div>

          <div class="form-field-wrapper">
            <FormKit
              type="menuselect"
              name="collectionSelection"
              label="Collection"
              v-model="collectionSelection"
              :options="collectionSelectOptions"
              placeholder="Select collection"
              outer-class="inline-field"
              max-width="160px"
              :teleport-menu="true"
              @input="handleCollectionSelectionChange"
              data-testid="collection-select"
            />
          </div>
        </div>
      </div>

      <div class="form-footer">
        <div class="form-actions">
          <div class="flex gap-3">
            <Button
              type="solid"
              variant="blue"
              :text="submitButtonText"
              :disabled="getSubmitDisabledState(state)"
              :class="{ 'pointer-events-none': getSubmitDisabledState(state) }"
              submit
              :data-testid="edit ? 'edit-request-content-save-button' : 'add-request-content-save-button'"
            />
            <Button
              type="ghost"
              variant="black"
              text="Cancel"
              @click="$emit('close')"
              :data-testid="edit ? 'edit-request-content-cancel-button' : 'add-request-content-cancel-button'"
            />
          </div>
          <!-- Edit URL Button (only for edit mode) -->
          <Button v-if="edit" type="link" variant="blue" text="Edit URL" icon="general.pencil" @click="$emit('editUrl')" data-testid="edit-url-button" />
        </div>
        <p v-if="incompleteCustomFieldCount" class="form-blocked-reason" data-testid="custom-fields-incomplete-reason">
          {{ incompleteCustomFieldCount }} custom {{ incompleteCustomFieldCount === 1 ? 'field needs' : 'fields need' }} a label or options before you can save.
        </p>
        <p v-else-if="!edit" class="form-note">Once created, the link is copied to your clipboard.</p>
      </div>
    </FormKit>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { Button, Icon } from '@/storybook';
import { FormKit } from '@formkit/vue';
import type { RequestContent } from '../../types';
import ContentTypeSelector from '../shared/ContentTypeSelector.vue';
import CustomFieldsBuilder from './CustomFieldsBuilder.vue';
import { countIncompleteCustomFields } from '../../utils/customFields';
import { useRequestContentModalForm } from '../../composables/useRequestContentModalForm';
import { useRequestContentCollectionSelection } from '../../composables/useRequestContentCollectionSelection';
import type { SavedFormState } from '../../composables/useRequestContentModalShared';
import type { CollectionItemInterface } from '@/store/useCollectionsStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';

interface Props {
  edit: boolean;
  item?: RequestContent | null;
  domain: string;
  selectedCollection?: CollectionItemInterface | null;
  savedFormState?: SavedFormState | null;
}

const props = defineProps<Props>();

interface Emits {
  save: [item: RequestContent];
  close: [];
  showCollectionPicker: [formState: SavedFormState];
  editUrl: [];
}

const emit = defineEmits<Emits>();

const organizationStore = useOrganizationStore();
const { requestContentLogEnabled, requestContentLogDomain } = storeToRefs(organizationStore);
const entitlementsStore = useEntitlementsStore();

// Domain selection for log collection
const logCollectionDomains = computed(() => organizationStore.requestContentSettings?.logCollectionDomains || []);
const hasMultipleDomains = computed(() => logCollectionDomains.value.length > 1);

// Selected domain for log collection
const selectedLogCollectionDomain = ref<string>('');

// Initialize selected domain
const initializeLogCollectionDomain = () => {
  if (props.item?.logCollectionDomain) {
    selectedLogCollectionDomain.value = props.item.logCollectionDomain;
  } else if (logCollectionDomains.value.length > 0) {
    selectedLogCollectionDomain.value = logCollectionDomains.value[0];
  } else {
    selectedLogCollectionDomain.value = requestContentLogDomain.value || '';
  }
};

// Initialize on mount
initializeLogCollectionDomain();

// Watch for changes in available domains (e.g., store loads async)
watch(logCollectionDomains, (newDomains, oldDomains) => {
  // Reinitialize when domains become available or the first domain changes
  const newFirstDomain = newDomains[0];
  const oldFirstDomain = oldDomains?.[0];

  if (!oldFirstDomain && newFirstDomain) {
    // Domains just loaded - initialize with first domain
    initializeLogCollectionDomain();
  } else if (newFirstDomain !== oldFirstDomain && !props.item?.logCollectionDomain) {
    // First domain changed and we're not editing an existing item
    selectedLogCollectionDomain.value = newFirstDomain || '';
  }
});

// Watch for changes in props.item (switching between create/edit)
watch(
  () => props.item,
  () => {
    initializeLogCollectionDomain();
  }
);

// Inline edit states
const inlineTitleEdit = ref(false);
const inlineMessageEdit = ref(false);

// Inline edit handlers
const startEditingTitle = () => {
  inlineTitleEdit.value = true;
  nextTick(() => {
    const input = document.querySelector('input[name="name"]') as HTMLInputElement | null;
    if (input) {
      input.focus();
    }
  });
};

const startEditingMessage = () => {
  inlineMessageEdit.value = true;
  nextTick(() => {
    const textarea = document.querySelector('textarea[name="message"]') as HTMLTextAreaElement | null;
    if (textarea) {
      textarea.focus();
    }
  });
};

const {
  formData,
  expirationOptions,
  submitButtonText,
  isNameValid,
  handleSubmit,
  getSystemDefaultName,
  getSystemDefaultMessage,
  getOrgDefaultTitle,
  getOrgDefaultDescription,
} = useRequestContentModalForm({
  edit: props.edit,
  item: props.item,
  domain: props.domain,
  organizationSettings: organizationStore.requestContentSettings,
  organizationName: organizationStore.name,
});

// Content-type-aware description and placeholder text
const formDescription = computed(() => {
  switch (formData.contentType) {
    case 'file':
      return 'Share a "request content" link to allow users to upload files without needing a Zight account. The files they upload will be stored in your account. You can manage and edit them later.';
    case 'image':
      return 'Share a "request content" link to allow users to upload screenshots without needing a Zight account. The screenshots they share will be stored in your account. You can manage and edit them later.';
    default:
      return 'Share a "request video" link to allow users to record Zight videos without needing a Zight account. The videos they record will be stored in your account. You can manage and edit them later.';
  }
});

const messagePlaceholder = computed(() => {
  switch (formData.contentType) {
    case 'file':
      return 'E.g. "Please upload the log file."';
    case 'image':
      return 'E.g. "Please take a screenshot of the error you are seeing and upload it."';
    default:
      return 'E.g. "Please record a video showing the bug you are experiencing. Include any info that may be relevant".';
  }
});

// Swap defaults and reset collect logs when switching content type
watch(
  () => formData.contentType,
  (newType, oldType) => {
    if (newType !== 'video') {
      formData.collectLogs = false;
    }

    // Swap title default if user hasn't customized it
    const oldDefault = getOrgDefaultTitle(oldType) || getSystemDefaultName(oldType);
    if (!formData.name?.trim() || formData.name === oldDefault) {
      formData.name = getOrgDefaultTitle(newType) || getSystemDefaultName(newType);
    }

    // Swap message default if user hasn't customized it
    const oldMessageDefault = getOrgDefaultDescription(oldType) || getSystemDefaultMessage(oldType);
    if (!formData.message?.trim() || formData.message === oldMessageDefault) {
      formData.message = getOrgDefaultDescription(newType) || getSystemDefaultMessage(newType);
    }
  }
);

// Restore form state from saved state on mount (when returning from collection picker)
onMounted(() => {
  if (props.savedFormState) {
    // Restore form data
    formData.name = props.savedFormState.name;
    formData.message = props.savedFormState.message;
    formData.customId = props.savedFormState.customId;
    formData.contentType = props.savedFormState.contentType;
    formData.expiresOption = props.savedFormState.expiresOption;
    formData.collectLogs = props.savedFormState.collectLogs;

    // Restore log collection domain
    selectedLogCollectionDomain.value = props.savedFormState.logCollectionDomain;

    // Restore inline edit states
    inlineTitleEdit.value = props.savedFormState.inlineTitleEdit;
    inlineMessageEdit.value = props.savedFormState.inlineMessageEdit;
  }
});

const incompleteCustomFieldCount = computed(() => countIncompleteCustomFields(formData.customFields));

// Dropdown options are plain chips outside FormKit, so FormKit's dirty flag misses option-only edits.
const customFieldsChanged = computed(() => JSON.stringify(formData.customFields || []) !== JSON.stringify(props.item?.customFields || []));

// Create a function to check if submit should be disabled based on FormKit state
const getSubmitDisabledState = (formState: any) => {
  const blocked = !formState.valid || !isNameValid.value || incompleteCustomFieldCount.value > 0 || (formData.collectLogs && !requestContentLogEnabled.value);
  if (!props.edit) {
    return blocked;
  }

  // Edit forms also need a change to save
  return blocked || (!formState.dirty && !customFieldsChanged.value);
};

// Collection selection composable
const {
  collectionSelection,
  collectionSelectOptions,
  handleCollectionSelectionChange: handleCollectionChange,
  updateCollectionFromPicker,
  getCollectionIdForSaving,
} = useRequestContentCollectionSelection(props.item?.collectionId);

// Initialize with existing collection if editing
if (props.item?.collectionId && props.edit) {
  // Collection will be initialized automatically by the composable
}

// Collect logs feature flag and settings
const logCollectionEnabled = computed(() => entitlementsStore.can('collectLogs'));

// Navigate to settings
const gotoRequestContentSettings = () => {
  const orgId = organizationStore.id;
  if (orgId) {
    window.location.href = `/organizations/${orgId}/settings/request`;
  }
};

// Collection selection handled by composable

const handleCollectionSelectionChange = (value: string) => {
  handleCollectionChange(value, () => {
    // Save current form state before showing collection picker
    const currentFormState: SavedFormState = {
      name: formData.name,
      message: formData.message,
      customId: formData.customId,
      contentType: formData.contentType,
      expiresOption: formData.expiresOption,
      collectLogs: formData.collectLogs,
      logCollectionDomain: selectedLogCollectionDomain.value,
      inlineTitleEdit: inlineTitleEdit.value,
      inlineMessageEdit: inlineMessageEdit.value,
    };
    emit('showCollectionPicker', currentFormState);
  });
};

const handleFormSubmit = (submittedData: any) => {
  // ContentTypeSelector is not a FormKit field, and the builder's FormKit list holds rows without keys, so both come from formData.
  const dataWithContentType = { ...submittedData, contentType: formData.contentType, customFields: formData.customFields };
  const cleanData = handleSubmit(dataWithContentType, getCollectionIdForSaving, selectedLogCollectionDomain.value);
  emit('save', cleanData);
};

// Update collection from picker when prop changes
watch(
  () => props.selectedCollection,
  newCollection => {
    if (newCollection) {
      updateCollectionFromPicker(newCollection);
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.form-container {
  @apply flex flex-col gap-6;
}

.form-description {
  @apply text-sm text-zinc-500 dark:text-zinc-400 mb-2 leading-relaxed;
}

.form-group {
  @apply mb-5;
}

.form-label {
  @apply block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2;
}

.help-text {
  @apply text-xs text-zinc-500 dark:text-zinc-400 mb-2;
}

/* The body scrolls inside the modal while the header above and this footer stay put.
   The subtracted space is the modal title plus this footer plus the modal's own padding. */
.form-body {
  @apply overflow-y-auto -ml-1 pl-1 -mr-4 pr-4;
  max-height: calc(100dvh - 2 * var(--modal-container-padding, 0px) - 16rem);
}

@media (min-width: 640px) {
  .form-body {
    max-height: calc(100dvh - 2 * var(--modal-container-padding, 0px) - 12rem);
  }
}

.form-footer {
  @apply pt-4 border-t border-zinc-200 dark:border-zinc-700;
}

.form-actions {
  @apply flex justify-between items-center;
}

.form-blocked-reason {
  @apply text-sm text-red-600 dark:text-red-400 mt-3 mb-0;
}

.form-note {
  @apply text-sm text-zinc-500 dark:text-zinc-400 mt-3 mb-0;
}

.form-row {
  @apply flex flex-row gap-4 items-start mb-5 mt-5;
}

.form-row-grid {
  @apply grid gap-4 items-start mb-5 mt-5;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

@media (min-width: 640px) {
  .form-row-grid {
    grid-template-columns: 200px 200px; /* Fixed widths: 200px for Expires, 200px for Collection */
    max-width: 75%; /* Reduce overall width to 75% of container */
  }
}

.form-field-wrapper {
  @apply min-w-0 max-w-full; /* Ensures the field can shrink below its content size and respect container width */
  /* Don't clip overflow here - let FormKit handle dropdown positioning */
}

/* The trigger's label truncates at a fixed width, so cap the trigger to its column on narrow screens. */
.form-field-wrapper :deep(.menu-select-wrapper),
.form-field-wrapper :deep(.menu-select-trigger) {
  @apply max-w-full;
}

.form-section {
  @apply flex flex-col gap-2 flex-1;
}

.form-label-inline {
  @apply text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2;
}

.inline-field {
  @apply flex-1;
}

/* Inline edit display mode styles */
.inline-display-mode {
  @apply flex items-center gap-2 cursor-pointer
         px-3 py-2 rounded-10
         ring-1 ring-slate-300 dark:ring-zinc-700
         bg-white dark:bg-zinc-800
         hover:ring-slate-400 dark:hover:ring-zinc-600
         transition-colors;
}

.display-text {
  @apply flex-1 text-zinc-900 dark:text-zinc-100 m-0;
}

.display-text-empty {
  @apply flex-1 text-zinc-400 dark:text-zinc-500 not-italic;
}

.edit-icon {
  @apply text-zinc-400 dark:text-zinc-500 flex-shrink-0;
}

.optional-text {
  @apply text-zinc-500 dark:text-zinc-400 font-normal;
}

/* Bootstrap-style switch styles */
.switch-container {
  @apply flex items-center gap-3 mb-3;
}

/* Collect logs section spacing */
.collect-logs-group {
  @apply pt-1;
}

.switch-wrapper {
  @apply relative inline-block cursor-pointer;
}

.switch-input {
  @apply sr-only;
}

.switch-slider {
  @apply relative inline-block w-12 h-6
         bg-zinc-300 dark:bg-zinc-600
         rounded-full transition-all duration-200
         before:content-[''] before:absolute before:top-0.5 before:left-0.5
         before:w-5 before:h-5 before:bg-white before:rounded-full
         before:transition-transform before:duration-200 before:shadow-md;
}

.switch-input:checked + .switch-slider {
  @apply bg-blue-500 dark:bg-blue-400;
}

.switch-input:checked + .switch-slider:before {
  transform: translateX(24px);
}

.switch-input:focus + .switch-slider {
  @apply ring-2 ring-blue-500 ring-opacity-50;
}

.switch-label {
  @apply text-base text-zinc-900 dark:text-zinc-100 m-0;
}

/* Collect logs info styles */
.collect-logs-info {
  @apply mt-3 p-3 bg-zinc-50 dark:bg-zinc-700 rounded-md;
}

.warning-message {
  @apply flex items-start gap-2 text-sm text-amber-600 dark:text-amber-400
         bg-amber-50 dark:bg-amber-900/20 p-3 rounded
         border border-amber-200 dark:border-amber-800;
}

.warning-icon {
  @apply flex-shrink-0 mt-0.5;
}

.warning-link {
  @apply font-medium underline hover:text-amber-700 dark:hover:text-amber-300 transition-colors;
}

.log-collection-domain {
  @apply text-sm text-zinc-600 dark:text-zinc-300;
}

.domain-text {
  @apply ml-1 text-zinc-800 dark:text-zinc-200;
}

/* Simple FormKit field width constraints for grid layout */
.form-field-wrapper :deep(.formkit-outer),
.form-field-wrapper :deep(.formkit-wrapper),
.form-field-wrapper :deep(.formkit-input) {
  @apply w-full;
}
</style>

<style>
/* The overlay's default padding (up to 5rem top and bottom) pushes Collect logs and Custom fields below
   the fold on shorter windows, so this modal trims it to give the scrolling body more room. */
@media (width >= 40rem) {
  .modal__container:has(.request-content-form) {
    --modal-container-padding: 1.5rem;
  }
}
</style>
